// ============================================
// Demo API - Mock Data Only (No Backend)
// ============================================

import {
  mockFactories,
  mockEquipments,
  mockFactoryLines,
  mockCompanyDashboard,
  mockCompanyFactoryOverview,
  mockFactoryDashboard,
  mockMachineDashboard,
  mockMachineOverview,
  mockMachineCycle,
  mockMachineTemperature,
  mockMachineDrive,
  mockMachineAlarm,
  mockMachineSpc,
  mockUsers,
  mockRoleGroups,
  mockPermissions,
  mockNotices,
  mockRecentAlarms,
  mockWidgetConfig,
  mockWidgetData,
  mockCustomWidgetData,
  mockShotHistory,
  mockWatch1Data,
  mockWatch2Data,
  mockETLStatus,
  mockETLHistory,
  mockLoginLogs,
  mockAIResponse,
  mockFactoryDetail,
  mockEquipmentDetail,
  mockUserDetail,
  mockDBConnections,
  mockLSTMAnalysis,
  mockDailyReport,
  mockWeeklyReport,
  mockMonthlyReport
} from './mockData';

// ============================================
// Utility Functions
// ============================================

// 비동기 딜레이 (실제 API 호출 시뮬레이션)
const delay = (ms = 300) => new Promise(resolve => setTimeout(resolve, ms));

// 사용자 정보 캐시
let cachedUser = null;
let cachedSchemaName = null;

export const getSchemaName = () => {
  const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
  if (cachedUser === userStr && cachedSchemaName) {
    return cachedSchemaName;
  }
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      cachedUser = userStr;
      cachedSchemaName = user.schemaName || 'seoro';
      return cachedSchemaName;
    } catch (e) {
      return 'seoro';
    }
  }
  return 'seoro';
};

export const clearApiCache = () => {
  cachedUser = null;
  cachedSchemaName = null;
};

export const getHeaders = () => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  return {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

export const getApiBaseUrl = () => 'mock://demo';
export const getBackendBaseUrl = () => 'mock://demo';
export const getAiApiBaseUrl = () => 'mock://demo';

export const getFullImageUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:')) {
    return path;
  }
  return '/images/placeholder.png';
};

export const refreshCsrfToken = () => null;

// ============================================
// Dashboard API
// ============================================

export const fetchEquipments = async () => {
  await delay();
  return mockEquipments;
};

export const fetchMachineOverview = async (equipmentId) => {
  await delay();
  const equipment = mockEquipments.find(e => e.id === parseInt(equipmentId));
  return {
    ...mockMachineOverview,
    equipmentId: parseInt(equipmentId),
    equipmentName: equipment?.name || `INJ-${equipmentId}`,
    status: equipment?.status || 'RUNNING'
  };
};

export const fetchMachineCycle = async (equipmentId) => {
  await delay();
  return mockMachineCycle;
};

export const fetchMachineTemperature = async (equipmentId) => {
  await delay();
  return mockMachineTemperature;
};

export const fetchMachineDrive = async (equipmentId) => {
  await delay();
  return mockMachineDrive;
};

export const fetchMachineAlarm = async (equipmentId) => {
  await delay();
  return mockMachineAlarm;
};

export const fetchMachineSpc = async (equipmentId, parameter = 'cycle_time') => {
  await delay();
  return { ...mockMachineSpc, parameter };
};

// 양/불 탭 - 샷 히스토리
export const fetchShotHistory = async (equipmentId, limit = 100) => {
  await delay();
  return { success: true, data: mockShotHistory };
};

// 감시1 탭
export const fetchWatch1Data = async (equipmentId) => {
  await delay();
  return { success: true, data: mockWatch1Data };
};

// 감시2 탭
export const fetchWatch2Data = async (equipmentId) => {
  await delay();
  return { success: true, data: mockWatch2Data };
};

// LSTM 불량 원인 분석 API
export const fetchLSTMAnalysis = async (equipmentId, craftId, shotNumber) => {
  await delay();
  return { success: true, data: mockLSTMAnalysis };
};

export const fetchFactories = async () => {
  await delay();
  return mockFactories;
};

export const fetchCompanyDashboard = async () => {
  await delay();
  return { ...mockCompanyDashboard, lastSyncTime: new Date().toISOString() };
};

export const fetchCompanyFactoryOverview = async () => {
  await delay();
  return mockCompanyFactoryOverview;
};

export const fetchFactoryDashboard = async (factoryId) => {
  await delay();
  return mockFactoryDashboard[factoryId] || mockFactoryDashboard[1];
};

export const fetchMachineDashboard = async (factoryId, lineId = null) => {
  await delay();
  const dashboardData = mockMachineDashboard[factoryId] || mockMachineDashboard[1];
  let machines = dashboardData.machines || [];
  if (lineId) {
    machines = machines.filter(m => m.lineId === parseInt(lineId));
  }
  return {
    machines,
    lastSyncTime: dashboardData.lastSyncTime
  };
};

export const fetchFactoryLines = async (factoryId) => {
  await delay();
  return mockFactoryLines[factoryId] || [];
};

export const fetchLineEquipments = async (factoryId, lineId) => {
  await delay();
  return mockEquipments.filter(e => e.factoryId === parseInt(factoryId) && e.lineId === parseInt(lineId));
};

// ============================================
// Widget API
// ============================================

export const fetchWidgetConfig = async (userId) => {
  await delay();
  return mockWidgetConfig;
};

export const saveWidgetConfig = async (userId, widgets) => {
  await delay();
  return { success: true };
};

export const addWidget = async (userId, widget) => {
  await delay();
  return { success: true };
};

export const fetchMachineWidget = async (equipmentId, widgetType) => {
  await delay();
  const endpointMap = {
    '온도': 'temperature',
    '압력': 'pressure',
    '에너지': 'energy',
    '기본정보': 'basic-info',
    '기본사출정보': 'injection-info',
    '알람이력': 'alarm-history'
  };
  const endpoint = endpointMap[widgetType] || widgetType;
  const rawData = mockWidgetData[endpoint];

  // 위젯 타입별 데이터 형식 맞추기
  if (widgetType === '기본정보') {
    return { basicInfo: rawData?.basicInfo || rawData };
  } else if (widgetType === '알람이력') {
    return rawData; // 배열 그대로 반환
  } else if (widgetType === '기본사출정보') {
    return rawData; // 객체 그대로 반환
  } else {
    // 온도, 압력, 에너지 등 차트용 배열 데이터
    return { data: rawData || [] };
  }
};

export const fetchCustomWidget = async (equipmentId, widgetId) => {
  await delay();
  // 사용자모듈 위젯용 샷 데이터 반환
  return { data: mockCustomWidgetData };
};

// ============================================
// Factory Management API
// ============================================

export const fetchFactoriesForManagement = async (page = 0, size = 1000) => {
  await delay();
  return mockFactories.map(f => {
    const detail = mockFactoryDetail[f.id] || {};
    return {
      id: f.id,
      factoryName: f.factoryName,
      factoryCode: detail.factoryCode || `FAC-00${f.id}`,
      address: f.address,
      temperature: detail.temperature || 22.5,
      humidity: detail.humidity || 45.0,
      totalEquipment: f.totalEquipment,
      runningEquipment: f.runningEquipment,
      idleEquipment: f.idleEquipment,
      stoppedEquipment: f.stoppedEquipment,
      operationRate: detail.operationRate || Math.round((f.runningEquipment / f.totalEquipment) * 100),
      status: detail.status || 'NORMAL',
      lastInspectionDate: detail.lastInspectionDate || '2025-10-15',
      managerName: detail.managerName || '담당자'
    };
  });
};

export const fetchFactoryDetailForManagement = async (factoryId) => {
  await delay();
  return mockFactoryDetail[factoryId] || mockFactoryDetail[1];
};

export const createFactory = async (factoryData) => {
  await delay();
  return { id: Date.now(), ...factoryData };
};

export const updateFactory = async (factoryId, factoryData) => {
  await delay();
  return { id: factoryId, ...factoryData };
};

export const deleteFactory = async (factoryId) => {
  await delay();
  return { success: true };
};

// ============================================
// Equipment Management API
// ============================================

export const fetchEquipmentsForManagement = async (page = 0, size = 1000) => {
  await delay();
  return mockEquipments;
};

export const fetchEquipmentDetailForManagement = async (equipmentId) => {
  await delay();
  return mockEquipmentDetail[equipmentId] || mockEquipmentDetail[1];
};

export const createEquipment = async (equipmentData) => {
  await delay();
  return { id: Date.now(), ...equipmentData };
};

export const updateEquipment = async (equipmentId, equipmentData) => {
  await delay();
  return { id: equipmentId, ...equipmentData };
};

export const deleteEquipment = async (equipmentId) => {
  await delay();
  return { success: true };
};

// ============================================
// User Management API
// ============================================

export const fetchUsersForManagement = async (page = 0, size = 1000) => {
  await delay();
  return mockUsers;
};

export const fetchUserDetailForManagement = async (userId) => {
  await delay();
  return mockUserDetail[userId] || mockUserDetail[1];
};

export const fetchRoleGroups = async (page = 0, size = 1000) => {
  await delay();
  return mockRoleGroups;
};

export const createUser = async (userData) => {
  await delay();
  return { id: Date.now(), ...userData };
};

export const updateUser = async (userId, userData) => {
  await delay();
  return { id: userId, ...userData };
};

export const deleteUser = async (userId) => {
  await delay();
  return { success: true };
};

// ============================================
// 권한 관리 API
// ============================================

export const fetchPermissions = async () => {
  await delay();
  return mockPermissions;
};

export const createRoleGroup = async (roleGroupData) => {
  await delay();
  return { id: Date.now(), ...roleGroupData };
};

export const updateRoleGroup = async (roleGroupId, roleGroupData) => {
  await delay();
  return { id: roleGroupId, ...roleGroupData };
};

export const deleteRoleGroup = async (roleGroupId) => {
  await delay();
  return { success: true };
};

// ============================================
// AI API
// ============================================

// 객체 배열을 TableRenderer 형식으로 변환
const convertToTableFormat = (dataArray) => {
  if (!dataArray || dataArray.length === 0) {
    return { columns: [], rows: [] };
  }
  const columns = Object.keys(dataArray[0]);
  const rows = dataArray.map(item => columns.map(col => item[col]));
  return { columns, rows };
};

// 추천 질문 응답 생성
const getRecommendedQuestionResponse = (message) => {
  // 질문 1: 현재 가동 중인 설비 현황은?
  if (message.includes('가동') && message.includes('설비') && message.includes('현황')) {
    const runningEquipments = mockEquipments.filter(e => e.status === 'RUNNING');
    const idleEquipments = mockEquipments.filter(e => e.status === 'IDLE');
    const stoppedEquipments = mockEquipments.filter(e => e.status === 'STOPPED');

    const tableData = mockEquipments.map(e => ({
      설비명: e.equipmentName || e.name,
      공장: e.factoryName || '본공장',
      라인: e.lineName || `라인${e.lineId}`,
      상태: e.status === 'RUNNING' ? '가동중' : e.status === 'IDLE' ? '대기중' : '정지',
      가동률: e.status === 'RUNNING' ? `${85 + Math.floor(Math.random() * 10)}%` : '-'
    }));

    return {
      intent: 'query',
      natural_language: `현재 총 ${mockEquipments.length}대의 설비 중 ${runningEquipments.length}대가 가동 중입니다.\n\n• 가동중: ${runningEquipments.length}대\n• 대기중: ${idleEquipments.length}대\n• 정지: ${stoppedEquipments.length}대\n\n전체 가동률은 ${Math.round((runningEquipments.length / mockEquipments.length) * 100)}%입니다.`,
      blocks: [{
        type: 'table',
        content: convertToTableFormat(tableData)
      }],
      success: true
    };
  }

  // 질문 2: 오늘 알람이 가장 많이 발생한 설비는?
  if (message.includes('알람') && message.includes('가장') && (message.includes('많이') || message.includes('발생'))) {
    // 알람 통계 생성
    const alarmStats = [
      { 설비명: 'INJ-003', 알람횟수: 12, 주요알람: '온도 이상', 마지막발생: '14:32' },
      { 설비명: 'INJ-001', 알람횟수: 8, 주요알람: '압력 경고', 마지막발생: '13:15' },
      { 설비명: 'INJ-005', 알람횟수: 5, 주요알람: '사이클 지연', 마지막발생: '11:48' },
      { 설비명: 'INJ-002', 알람횟수: 3, 주요알람: '계량 이상', 마지막발생: '09:22' },
      { 설비명: 'INJ-004', 알람횟수: 1, 주요알람: '통신 오류', 마지막발생: '08:05' }
    ];

    return {
      intent: 'query',
      natural_language: `오늘 알람이 가장 많이 발생한 설비는 **INJ-003**으로 총 12건의 알람이 발생했습니다.\n\n주요 알람 유형은 '온도 이상'이며, 가장 최근 알람은 14:32에 발생했습니다.\n\n해당 설비의 온도 센서 점검을 권장합니다.`,
      blocks: [{
        type: 'table',
        content: convertToTableFormat(alarmStats)
      }],
      success: true
    };
  }

  // 질문 3: 최근 7일간 생산량 추이를 선그래프로 보여줘
  if (message.includes('7일') && message.includes('생산량') && (message.includes('추이') || message.includes('그래프'))) {
    const today = new Date();
    const productionData = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(today);
      date.setDate(date.getDate() - (6 - i));
      const dayOfWeek = date.getDay();
      // 주말은 생산량 감소
      const baseProduction = dayOfWeek === 0 || dayOfWeek === 6 ? 3500 : 5200;
      return {
        date: `${date.getMonth() + 1}/${date.getDate()}`,
        생산량: baseProduction + Math.floor(Math.random() * 800),
        목표: 5000
      };
    });

    const totalProduction = productionData.reduce((sum, d) => sum + d.생산량, 0);
    const avgProduction = Math.round(totalProduction / 7);

    return {
      intent: 'query',
      natural_language: `최근 7일간 총 생산량은 ${totalProduction.toLocaleString()}개이며, 일평균 ${avgProduction.toLocaleString()}개입니다.\n\n목표 대비 달성률은 ${Math.round((avgProduction / 5000) * 100)}%입니다.`,
      blocks: [{
        type: 'chart',
        content: {
          type: 'line',
          title: '최근 7일 생산량 추이',
          data: productionData,
          xAxisKey: 'date',
          yAxisKeys: ['생산량', '목표'],
          options: { height: 300 }
        }
      }],
      success: true
    };
  }

  // 질문 4: 사출 1호기에 최근 샷 온도 변화에 대해서 분석해줘
  if (message.includes('사출') && message.includes('1호기') && message.includes('온도') && (message.includes('분석') || message.includes('변화'))) {
    const tempData = Array.from({ length: 20 }, (_, i) => ({
      샷번호: 1580 + i,
      노즐온도: Math.round((218 + Math.random() * 4 - 2) * 10) / 10,
      실린더1: Math.round((212 + Math.random() * 3 - 1.5) * 10) / 10,
      실린더2: Math.round((208 + Math.random() * 3 - 1.5) * 10) / 10,
      실린더3: Math.round((205 + Math.random() * 4 - 2) * 10) / 10,
      금형온도: Math.round((85 + Math.random() * 2 - 1) * 10) / 10
    }));

    return {
      intent: 'query',
      natural_language: `**사출 1호기 온도 분석 결과**\n\n최근 20샷의 온도 데이터를 분석한 결과:\n\n• **노즐온도**: 평균 218.2°C (설정값 220°C 대비 -0.8%)\n• **실린더1**: 평균 212.1°C - 정상 범위\n• **실린더2**: 평균 208.3°C - 정상 범위  \n• **실린더3**: 평균 205.5°C - 정상 범위\n• **금형온도**: 평균 85.2°C - 정상 범위\n\n📊 **분석 의견**: 전체적으로 온도가 안정적으로 유지되고 있습니다. 노즐온도가 설정값보다 약간 낮게 유지되고 있어 모니터링이 필요합니다.`,
      blocks: [{
        type: 'chart',
        content: {
          type: 'line',
          title: '사출 1호기 온도 변화 추이',
          data: tempData,
          xAxisKey: '샷번호',
          yAxisKeys: ['노즐온도', '실린더1', '실린더2', '실린더3', '금형온도'],
          options: { height: 300 }
        }
      }],
      success: true
    };
  }

  return null;
};

export const sendAgentMessage = async (message, conversationHistory = []) => {
  await delay(1000);

  // 추천 질문에 대한 응답 확인
  const recommendedResponse = getRecommendedQuestionResponse(message);
  if (recommendedResponse) {
    return recommendedResponse;
  }

  // 그 외 모든 질문에 대해서는 관리자 문의 안내
  return {
    intent: 'general',
    natural_language: '현재 데모 페이지로 추천 질문만 가능합니다.\n\n실제 서비스에서는 사용자의 질문으로 AI 분석이 가능합니다.\n\n추가 이용 문의: ceo@seoro.ai.kr',
    blocks: [],
    success: true
  };
};

// 스트리밍 시뮬레이션 (데모용)
export const sendAgentMessageStream = async (message, conversationHistory = [], onToken, onStatus, onComplete) => {
  // 상태 업데이트 시뮬레이션
  const statusMessages = [
    '의도 분석 중...',
    '의도: data_query',
    '데이터 조회 중...',
    '결과 생성 중...'
  ];

  for (const status of statusMessages) {
    onStatus(status);
    await delay(300);
  }

  // 추천 질문에 대한 응답 확인
  const response = getRecommendedQuestionResponse(message) || {
    intent: 'general',
    natural_language: '현재 데모 페이지로 추천 질문만 가능합니다.\n\n실제 서비스에서는 사용자의 질문으로 AI 분석이 가능합니다.\n\n추가 이용 문의: ceo@seoro.ai.kr',
    blocks: [],
    success: true
  };

  // 텍스트 스트리밍 시뮬레이션 (글자 단위로 출력)
  const text = response.natural_language || '';
  let currentText = '';

  for (let i = 0; i < text.length; i++) {
    currentText += text[i];
    onToken(text[i], currentText);
    // 랜덤 딜레이로 자연스러운 타이핑 효과
    await delay(15 + Math.random() * 25);
  }

  // 완료
  await delay(200);
  onComplete(response);
};

export const sendAIMessage = async (message, conversationHistory = []) => {
  const result = await sendAgentMessage(message, conversationHistory);
  return {
    response: result.natural_language,
    sqlQuery: null,
    data: result.blocks,
    status: result.success ? 'success' : 'error'
  };
};

// ============================================
// 기타 API
// ============================================

export const updateTargetProduction = async (targetProduction) => {
  await delay();
  return { success: true };
};

export const fetchNotices = async (limit = 10) => {
  await delay();
  return mockNotices.slice(0, limit);
};

export const fetchAllNotices = async (page = 0, size = 1000) => {
  await delay();
  return mockNotices;
};

export const fetchNoticeDetail = async (id) => {
  await delay();
  return mockNotices.find(n => n.id === parseInt(id)) || mockNotices[0];
};

export const createNotice = async (data) => {
  await delay();
  return { id: Date.now(), ...data };
};

export const updateNotice = async (id, data) => {
  await delay();
  return { id, ...data };
};

export const deleteNotice = async (id) => {
  await delay();
  return { success: true };
};

export const fetchRecentAlarms = async (limit = 20) => {
  await delay();
  const alarms = mockRecentAlarms.slice(0, limit);

  // Header.jsx가 기대하는 형식으로 반환
  const errorCount = alarms.filter(a => a.severity === 'ERROR').length;
  const warningCount = alarms.filter(a => a.severity === 'WARNING').length;
  const infoCount = alarms.filter(a => a.severity === 'INFO').length;

  return {
    totalCount: alarms.length,
    errorCount,
    warningCount,
    infoCount,
    recentAlarms: alarms.map(a => ({
      ...a,
      alarmId: a.id
    }))
  };
};

// ============================================
// ETL / System API
// ============================================

export const fetchETLStatus = async () => {
  await delay();
  return mockETLStatus;
};

export const triggerETLSync = async () => {
  await delay();
  return { success: true, message: '동기화가 시작되었습니다.' };
};

export const fetchETLHistory = async (limit = 20) => {
  await delay();
  return mockETLHistory.slice(0, limit);
};

export const fetchDBConnections = async () => {
  await delay();
  return mockDBConnections;
};

export const fetchETLSyncHistory = async (limit = 20) => {
  await delay();
  return mockETLHistory.slice(0, limit);
};

export const fetchLoginLogs = async (page = 0, size = 1000, eventType = null) => {
  await delay();
  let logs = mockLoginLogs;
  if (eventType) {
    logs = logs.filter(l => l.eventType === eventType);
  }
  return logs;
};

export const fetchFailedLoginLogs = async () => {
  await delay();
  return mockLoginLogs.filter(l => !l.success);
};

// ============================================
// System Settings API (Demo)
// ============================================

const mockSystemSettings = {
  systemName: '사출 통합 관제 시스템',
  companyName: '(주)서로',
  companyCode: 'SEORO',
  schemaName: 'seoro',
  businessNumber: '123-45-67890',
  representativeName: '김대표',
  contactEmail: 'ceo@seoro.ai.kr',
  contactPhone: '032-721-7118',
  address: '인천광역시 연수구 송도미래로11번길27, 스마트스퀘어 A동 1501호',
  logoUrl: '',
  faviconUrl: '',
  refreshInterval: '10초',
  timezone: 'Asia/Seoul',
  maxLoginAttempts: 5,
  lockDurationMinutes: 30,
  notifyWeb: true,
  notifyEmail: false,
  notifySms: false,
  notifyPush: false
};

export const fetchSystemSettings = async () => {
  await delay();
  return { success: true, data: mockSystemSettings };
};

export const saveSystemSettings = async (settings) => {
  await delay();
  return { success: true, message: '설정이 저장되었습니다.' };
};

export const uploadLogo = async (file) => {
  await delay();
  return { success: true, data: { logoUrl: '/images/demo-logo.png' } };
};

export const uploadFavicon = async (file) => {
  await delay();
  return { success: true, data: { faviconUrl: '/images/demo-favicon.png' } };
};

// ============================================
// Realtime Shot Status API (Demo)
// ============================================

export const fetchRealtimeShotStatus = async () => {
  await delay();

  // mockEquipments 기반으로 실시간 샷 데이터 생성
  const shotData = mockEquipments.map((e, idx) => ({
    equipmentId: e.id,
    equipmentName: e.equipmentName,
    craftId: ['기어 하우징', '커넥터 케이스', '버튼 캡', '센서 커버'][idx % 4],
    shotCount: 1500 + Math.floor(Math.random() * 5000),
    updateDateTime: new Date().toISOString(),
    tmInjSpeed1: Math.round((80 + Math.random() * 40) * 10) / 10,
    tmInjPress1: Math.round((120 + Math.random() * 60) * 10) / 10,
    tmInjPosi1: Math.round((50 + Math.random() * 30) * 10) / 10,
    tmHoldSpeed1: Math.round((20 + Math.random() * 20) * 10) / 10,
    tmHoldPress1: Math.round((80 + Math.random() * 40) * 10) / 10,
    tmChargePress1: Math.round((60 + Math.random() * 30) * 10) / 10,
    tmChargePosi1: Math.round((100 + Math.random() * 50) * 10) / 10,
    tmInjEndPosi: Math.round((10 + Math.random() * 10) * 10) / 10,
    tmTemp1Current: Math.round((200 + Math.random() * 30) * 10) / 10,
    tmTemp2Current: Math.round((210 + Math.random() * 25) * 10) / 10,
    tmTemp3Current: Math.round((205 + Math.random() * 28) * 10) / 10,
    tmTemp4Current: Math.round((195 + Math.random() * 35) * 10) / 10,
    tmTemp5Current: Math.round((190 + Math.random() * 20) * 10) / 10,
    previousShot: {
      shotCount: 1500 + Math.floor(Math.random() * 5000) - 1,
      tmInjSpeed1: Math.round((80 + Math.random() * 40) * 10) / 10,
      tmInjPress1: Math.round((120 + Math.random() * 60) * 10) / 10,
      tmTemp1Current: Math.round((200 + Math.random() * 30) * 10) / 10,
    }
  }));

  return { success: true, data: shotData };
};

// ============================================
// Report API (일일/주간/월간 보고서)
// ============================================

export const fetchDailyReport = async () => {
  await delay(500);
  return mockDailyReport;
};

export const fetchWeeklyReport = async () => {
  await delay(500);
  return mockWeeklyReport;
};

export const fetchMonthlyReport = async () => {
  await delay(500);
  return mockMonthlyReport;
};
